import React, { useEffect, useState } from 'react';
import { getAdminEngagements, verifyBrand } from '../api';

const STATUS_COLOR = {
  requested: 'var(--s-faint)', uploaded: 'var(--s-blue)',
  in_review: 'var(--s-amber)', training: 'var(--s-amber)', live: 'var(--s-green)',
};

export default function AdminPanel() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);   // user_id being toggled

  const load = () => {
    setLoading(true);
    getAdminEngagements()
      .then(d => { setRows(d.engagements || []); setError(null); })
      .catch(e => setError(e.status === 403 ? 'Admin access required.' : (e.message || 'Failed to load')))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggle = async (r) => {
    setBusy(r.user_id);
    try {
      await verifyBrand(r.user_id, !r.brand_verified);
      setRows(rs => rs.map(x => x.user_id === r.user_id ? { ...x, brand_verified: !x.brand_verified } : x));
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally { setBusy(null); }
  };

  const pending = (rows || []).filter(r => !r.brand_verified).length;

  return (
    <div style={styles.wrap}>
      <div style={styles.head}>
        <h2 style={styles.h2}>Brand verification</h2>
        <p style={styles.sub}>
          Confirm brand ownership before a company can upload sales data or access its
          model. {pending > 0 && <b style={{ color: 'var(--s-amber)' }}>{pending} pending.</b>}
        </p>
      </div>

      {loading && <div style={styles.muted}>Loading…</div>}
      {error && <div style={styles.err}>{error}</div>}

      {rows && rows.length === 0 && !loading && (
        <div style={styles.muted}>No predictive-analytics engagements yet.</div>
      )}

      {rows && rows.length > 0 && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead><tr>
              {['Company / user', 'Brand', 'Status', 'Stores', 'Verified', ''].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.user_id} style={r.brand_verified ? undefined : styles.pendingRow}>
                  <td style={styles.td}>
                    <div style={styles.company}>{r.company || r.name || '—'}</div>
                    <div style={styles.email}>{r.email || r.name || ''}</div>
                  </td>
                  <td style={styles.td}><span style={styles.brand}>{r.brand}</span></td>
                  <td style={styles.td}>
                    <span style={{ ...styles.status, color: STATUS_COLOR[r.status] || 'var(--s-muted)' }}>
                      {r.status}
                    </span>
                  </td>
                  <td style={{ ...styles.td, fontFamily: 'var(--s-mono)' }}>{r.stores_uploaded}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(r.brand_verified ? styles.badgeYes : styles.badgeNo) }}>
                      {r.brand_verified ? 'verified' : 'pending'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      className={`sq-btn ${r.brand_verified ? '' : 'primary'}`}
                      onClick={() => toggle(r)}
                      disabled={busy === r.user_id}
                      style={{ padding: '6px 12px', fontSize: 12 }}
                    >
                      {busy === r.user_id ? '…' : r.brand_verified ? 'Revoke' : 'Verify'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { padding: '26px 30px 40px', maxWidth: 860, margin: '0 auto' },
  head: { marginBottom: 20 },
  h2: { fontFamily: 'var(--s-serif)', fontSize: 24, fontWeight: 700, color: 'var(--s-text)' },
  sub: { fontSize: 13.5, color: 'var(--s-muted)', lineHeight: 1.6, marginTop: 6 },
  muted: { fontSize: 13, color: 'var(--s-faint)', padding: '8px 0' },
  err: { fontSize: 13, color: 'var(--s-red)', background: 'rgba(255,93,93,0.08)', border: '1px solid rgba(255,93,93,0.25)', borderRadius: 10, padding: '11px 13px' },
  tableWrap: { border: '1px solid var(--s-line)', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 14px', font: '600 9.5px var(--s-sans)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--s-faint)', borderBottom: '1px solid var(--s-line)', background: 'var(--s-w02)' },
  td: { padding: '11px 14px', color: 'var(--s-text)', borderBottom: '1px solid var(--s-line2)', verticalAlign: 'middle' },
  pendingRow: { background: 'rgba(255,176,46,0.04)' },
  company: { fontWeight: 600, fontSize: 13 },
  email: { fontSize: 11, color: 'var(--s-faint)', marginTop: 1 },
  brand: { fontFamily: 'var(--s-mono)', fontSize: 12.5, color: 'var(--s-text)' },
  status: { fontFamily: 'var(--s-mono)', fontSize: 11.5, textTransform: 'capitalize' },
  badge: { font: '600 9.5px var(--s-sans)', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99 },
  badgeYes: { background: 'rgba(61,220,132,0.13)', color: 'var(--s-green)' },
  badgeNo: { background: 'rgba(255,176,46,0.13)', color: 'var(--s-amber)' },
};
